/**
 * Pilot lifecycle — create, delete, and the lossless whole-`PilotData` restore.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import type { RosterLeafCommand } from "../commands";
import { seededGmsLicense } from "../licenses";
import type { PilotData, RosterState } from "../types";
import { changed, requirePilot, withPilot, type RosterApplyResult } from "./context";

export function applyCreatePilot(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "create_pilot" }>,
): Result<RosterApplyResult, CommandError> {
  if (state.pilots[cmd.pilotId]) {
    return err(new CommandError(`create_pilot: pilot ${cmd.pilotId} already exists`));
  }
  const pilot: PilotData = {
    id: cmd.pilotId,
    callsign: cmd.callsign,
    name: cmd.name,
    level: 0,
    mechSkills: { hull: 0, agi: 0, sys: 0, eng: 0 },
    skills: {},
    talents: {},
    // Seed the free, max-rank GMS license so the equip gate needs no GMS
    // special-case (the migration seeds the same for existing pilots).
    licenses: [seededGmsLicense()],
    // No core bonuses until the pilot takes one (budget = floor(level/3)).
    coreBonuses: [],
    // No pilot personal gear until the pilot picks some (limits 2/1/3 per category).
    gear: [],
    mechs: [],
    // No printed-mech wear yet — every mech reads pristine until a fight commits.
    printedMechs: {},
    // No explicit active mech yet; resolveActiveMechId falls back to the first mech.
    activeMechId: null,
    owner: cmd.owner,
  };
  return ok({
    state: withPilot(state, pilot),
    inverse: { type: "delete_pilot", actorId: cmd.actorId, pilotId: cmd.pilotId },
    events: changed(cmd.pilotId),
  });
}

export function applyDeletePilot(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "delete_pilot" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const { [cmd.pilotId]: _removed, ...rest } = state.pilots;
  // Inverse: splice the captured raw pilot back in via the internal
  // `restore_pilot` command. Rebuilding the pilot from public commands
  // (create + re-set level/skills/mechs/frames/weapons) would be lossy —
  // mech/loadout ids and equipped layouts wouldn't survive — so we capture
  // the whole `PilotData` (already wire-shaped raw data) for a lossless undo.
  return ok({
    state: { ...state, pilots: rest },
    inverse: { type: "restore_pilot", actorId: cmd.actorId, pilot },
    events: changed(cmd.pilotId),
  });
}

export function applyRestorePilot(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "restore_pilot" }>,
): Result<RosterApplyResult, CommandError> {
  // Internal inverse of delete_pilot: splice a captured raw pilot back in
  // verbatim (lossless). Its own inverse is delete_pilot, so undo/redo of a
  // delete round-trips exactly.
  if (state.pilots[cmd.pilot.id]) {
    return err(new CommandError(`restore_pilot: pilot ${cmd.pilot.id} already exists`));
  }
  return ok({
    state: withPilot(state, cmd.pilot),
    inverse: { type: "delete_pilot", actorId: cmd.actorId, pilotId: cmd.pilot.id },
    events: changed(cmd.pilot.id),
  });
}
